import {
  ApiBidRequest,
  ApiBidResponse,
  ApiError as ApiErrorType,
} from "../types/api"
import { ApiError, formatApiError } from "./apiUtils"
import { postProtected } from "../../../api/auth"

class BidsApiError extends ApiError {
  constructor(message: string, status?: number, apiError?: ApiErrorType) {
    super(message, status, apiError)
    this.name = "BidsApiError"
  }
}

// Validate bid data before API call
const validateBidData = (bidData: ApiBidRequest): string[] => {
  const errors: string[] = []

  if (!bidData.bid_type) {
    errors.push("Bid type is required")
  }

  if (bidData.bid_type === "lump sum") {
    if (!bidData.total_amount || parseFloat(bidData.total_amount) <= 0) {
      errors.push("Total amount must be greater than 0 for lump sum bids")
    }
  }

  if (bidData.bid_type === "pay as cut") {
    if (!bidData.product_prices || bidData.product_prices.length === 0) {
      errors.push("Product prices are required for pay-as-cut bids")
    } else {
      // Check that at least one product has a price > 0
      const hasNonZeroPrice = bidData.product_prices.some(
        (p) => p.price_per_unit > 0
      )
      if (!hasNonZeroPrice) {
        errors.push("At least one product must have a price greater than 0")
      }

      // Validate individual product prices
      bidData.product_prices.forEach((price, index) => {
        if (!price.product_name?.trim()) {
          errors.push(`Product ${index + 1}: Product name is required`)
        }
        if (price.price_per_unit < 0) {
          errors.push(`Product ${index + 1}: Price cannot be negative`)
        }
      })
    }
  }

  return errors
}

// Main API function to create a bid
export const createBid = async (
  saleId: number,
  bidData: ApiBidRequest
): Promise<ApiBidResponse> => {
  // Client-side validation
  const validationErrors = validateBidData(bidData)
  if (validationErrors.length > 0) {
    throw new BidsApiError(
      `Validation failed:\n${validationErrors.join("\n")}`,
      400
    )
  }

  try {
    // Make API request using protected endpoint
    const response = await postProtected(
      `birddog/sales/${saleId}/bids/`,
      bidData,
      undefined,
      "birddog"
    )

    const createdBid: ApiBidResponse = response.data
    return createdBid
  } catch (error: any) {
    // Re-throw our custom errors
    if (error instanceof BidsApiError) {
      throw error
    }

    // Handle API errors from protected endpoints
    if (error?.response?.data) {
      const formattedError = formatApiError(error.response.data as ApiErrorType)
      throw new BidsApiError(
        formattedError,
        error.response.status as number,
        error.response.data as ApiErrorType
      )
    }

    // Handle network errors or other unexpected errors
    if (error instanceof Error) {
      throw new BidsApiError(`Network error: ${error.message}`, 0)
    }

    throw new BidsApiError("An unexpected error occurred")
  }
}

export { BidsApiError }
